import { type AmpPageType, ContentTypeName, DataPickerContentType, PageType } from '@/constants';
import { pageOverviewBannerQuery, seoSectionQuery } from '@/fetchers';
import {
  DataPickerFieldType,
  DataPickerUiTemplate,
  HighlightDisplayLogic,
  type IHighlightSection,
  type ILandingPage,
  type IThumbnailCarousel,
  type IThumbnailSection,
  type IWrapper,
  type ThumbnailProps,
} from '@/interfaces';
import { landingPageDataModify, minifyQuery, thumbnailSectionDataModify } from '@/utils';
import {
  dataPickerEntryHandler,
  dataPickerRetailerHandler,
  utils_getThumbnailsByTag,
  utils_getThumbnailsByTagList,
} from '../utils';
import callContentful from './base.fetchers';

const THUMBNAIL_CAROUSEL_MAX_ITEMS = 30;
type WrapperWithDataPicker = IWrapper & { dataPicker: NonNullable<IWrapper['dataPicker']> };

type GetLandingPage = (args: {
  isPreviewMode: boolean;
  ampPageType?: AmpPageType;
  slug?: string;
  allArticlesList: ThumbnailProps[];
  accessToken?: string;
}) => Promise<ILandingPage | null>;

export const getLandingPage: GetLandingPage = async ({
  isPreviewMode,
  ampPageType,
  slug,
  allArticlesList,
  accessToken,
}) => {
  const { getThumbnailSectionData } = thumbnailSectionDataModify;

  const query = /* GraphQL */ `
    {
      landingPageCollection(
        preview: ${isPreviewMode}
        where: {
          ${ampPageType ? `ampPageType: "${ampPageType}"` : ''}
          ${slug ? `slug: "${slug}"` : 'slug_exists: false'}
        }
        limit: 1
      ) {
        items {
          sys { id }
          slug
          title
          breadcrumbsTheme
          onlyLogo
          ampPageType
          seoPageType
          faq
          trackingSolution
          pageOverviewBanner
          pageOverviewBannerCtaButtons: pageOverviewBannerCtaButtonsCollection(limit: 3) {
            items {
              sys { id }
              primaryButtonText
              secondaryButtonText
              function
              color
              buttonLink
              appStoreUrl
              googlePlayUrl
              email
              openInNewTab
              hideOnLoggedIn
            }
          }
          landingPageSeo
          supportedLocales
          content {
            json
            links {
              assets {
                block {
                  sys {
                    id
                  }
                  __typename
                  contentType
                  url
                  title
                  description
                  height
                  width
                }
              }
            }
          }
          sectionList: sectionListCollection(
            preview: ${isPreviewMode}
            limit: 15
          ) {
            items {
              __typename
              ... on Wrapper {
                title
                subText
                subTextMobile
                backgroundColor
                image {
                  contentType
                  url
                  description
                }
                content {
                  json
                  links {
                    assets {
                      block {
                        sys {
                          id
                        }
                        __typename
                        contentType
                        url
                        title
                        description
                        height
                        width
                      }
                    }
                  }
                }
                entryList: entryListCollection(limit: 20) {
                  items {
                    __typename
                    ... on ContentItem {
                      uiTemplate
                      title
                      subTitle
                      longSubTitle
                      image {
                        contentType
                        url(transform: {
                          width: 560,
                        })
                        description
                      }
                      buttonText
                      buttonLink
                      openInNewTab
                    }
                  }
                }
                dataPicker
              }
              ... on HighlightSection {
                title
                displayingLogic
                tags: tagsCollection(limit: 5) {
                  items {
                    sys {
                      id
                    }
                    title
                    type
                    slug
                  }
                }
              }
              ${seoSectionQuery()}
              ... on LeadGenBanner {
                title
                titleMobile
                image {
                  contentType
                  url(transform: {
                    width: 1261,
                  })
                  description
                }
                imageMobile {
                  contentType
                  url(transform: {
                    width: 696,
                  })
                  description
                }
                subCopy
                subCopyMobile
                buttons: buttonsCollection(limit: 3) {
                  items {
                    sys {
                      id
                    }
                    primaryButtonText
                    secondaryButtonText
                    function
                    color
                    email
                    buttonLink
                    appStoreUrl
                    googlePlayUrl
                    buttonEventLabel
                    openInNewTab
                  }
                }
                sectionTheme: theme
                isLinkedInBanner
              }
              ${pageOverviewBannerQuery()}
            }
          }
          seo {
            title
            description
            ogType
            ogUrl
            ogImage {
              contentType
              url
            }
            ogTitle
            ogDescription
            robots
          }
        }
      }
    }
  `;

  const _slug = `/${slug}` || 'homepage';
  const response = await callContentful(
    minifyQuery(query),
    isPreviewMode,
    _slug,
    'landingPage.fetchers.getLandingPage',
  );
  const landingPageData = landingPageDataModify(response.data?.landingPageCollection.items?.filter(Boolean)?.[0]);

  if (!landingPageData) return null;

  const sectionItems = landingPageData?.sectionList?.filter(Boolean);

  const foundHighlightSection: IHighlightSection = sectionItems?.find(
    (section: any) => section.__typename === ContentTypeName.HighlightSection,
  );

  const foundThumbnailSection: IThumbnailSection = sectionItems?.find(
    (section: any) => section.__typename === ContentTypeName.ThumbnailSection,
  );

  const foundThumbnailCarousel: IThumbnailCarousel[] = sectionItems?.filter(
    (section: any) => section.__typename === ContentTypeName.ThumbnailCarousel,
  );

  const foundWrapperUsingDataPicker: WrapperWithDataPicker[] = sectionItems?.filter(
    (section: any) =>
      section.__typename === ContentTypeName.Wrapper &&
      !section?.entryList?.items?.length &&
      !section?.content &&
      !!section?.dataPicker,
  );

  const foundWrapperUsingThumbnailSection: IWrapper[] = sectionItems?.filter(
    (section: any) =>
      section.__typename === ContentTypeName.Wrapper &&
      section?.entryList?.items?.length &&
      section?.entryList?.items?.[0]?.__typename === ContentTypeName.ThumbnailSection,
  );

  if (foundWrapperUsingDataPicker.length > 0) {
    await Promise.all(
      foundWrapperUsingDataPicker.map(async (section) => {
        const { dataPicker } = section;

        const contentType = dataPicker.contentType || DataPickerContentType.carrier;
        const uiTemplate = dataPicker.uiTemplate || DataPickerUiTemplate.Table;
        const fields = dataPicker.fields?.filter(({ show }) => show);
        const foundGroupByField = dataPicker.fields?.find(({ type }) => type === DataPickerFieldType.GroupBy);

        section.dataPicker = {
          ...dataPicker,
          contentType,
          uiTemplate,
          fields,
        };

        if (foundGroupByField) {
          section.dataPicker.groupBy = foundGroupByField.id;
          section.dataPicker.sortDirection = foundGroupByField.sortDirection; // only support sort for GroupBy field
        }

        const isRetailer = contentType === DataPickerContentType.retailer;

        // NOTE: No case of retailer showing `default` fields yet => Not call API for now
        const mappedData = isRetailer
          ? await dataPickerRetailerHandler({
              fields,
              accessToken,
            })
          : await dataPickerEntryHandler({
              slug: slug || '',
              dataPicker: section.dataPicker,
            });

        section.data = mappedData.data;
        section.dataPicker.pagination = isRetailer ? mappedData.pagination : null;
      }),
    );
  }

  const hasArticles =
    foundHighlightSection ||
    foundThumbnailSection ||
    foundThumbnailCarousel.length > 0 ||
    foundWrapperUsingThumbnailSection.length > 0;

  const allArticles = hasArticles ? allArticlesList || [] : [];

  if (foundHighlightSection) {
    const filteredTags = foundHighlightSection.tags?.items?.filter(Boolean);
    let highlightSectionThumbnails: ThumbnailProps[] = [];

    if (filteredTags?.length > 0) {
      highlightSectionThumbnails = utils_getThumbnailsByTagList({
        isFeatured: foundHighlightSection.displayingLogic === HighlightDisplayLogic.Featured,
        articles: allArticles,
        tags: filteredTags,
      });
    }

    foundHighlightSection.thumbnails = highlightSectionThumbnails.slice(0, 4);
  }

  if (foundThumbnailCarousel.length > 0) {
    foundThumbnailCarousel.map((item) => {
      const el = utils_getThumbnailsByTag({
        articles: allArticles,
        type: item.tag.type,
        slug: item.tag.slug,
      });
      item.thumbnails = el.slice(0, THUMBNAIL_CAROUSEL_MAX_ITEMS);
    });
  }

  if (foundWrapperUsingThumbnailSection.length) {
    foundWrapperUsingThumbnailSection.forEach(({ entryList }: any) => {
      if (!entryList?.items?.length) return;

      entryList.items = entryList.items.map((data: any, index: number) => {
        if (index !== 0 || !data) return data;

        if (data.featuredArticles?.items?.length) {
          data.featuredArticles = data.featuredArticles?.items.filter(Boolean) || [];
          return data;
        }

        return getThumbnailSectionData({
          thumbnailSection: data,
          highlightSection: foundHighlightSection,
          allArticles,
          pageType: PageType.LandingPage,
        });
      });
    });
  }

  if (foundThumbnailSection) {
    const newThumbnailSection = getThumbnailSectionData({
      thumbnailSection: foundThumbnailSection,
      highlightSection: foundHighlightSection,
      allArticles,
      pageType: PageType.LandingPage,
    });

    const foundIndex = sectionItems.findIndex(
      (section: any) => section.__typename === ContentTypeName.ThumbnailSection,
    );

    if (foundIndex > -1) {
      landingPageData.sectionList.items[foundIndex] = newThumbnailSection;
    }
  }

  return landingPageData;
};
